import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { catalogApi } from '../../api';
import { X, CheckCircle, Loader2, AlertCircle, AlertTriangle } from 'lucide-react';

interface PublishCatalogDialogProps {
  isOpen: boolean;
  onClose: () => void;
  version: number;
  onSuccess?: () => void;
}

export function PublishCatalogDialog({
  isOpen,
  onClose,
  version,
  onSuccess,
}: PublishCatalogDialogProps) {
  const queryClient = useQueryClient();

  const [notes, setNotes] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [showSuccess, setShowSuccess] = useState(false);

  // Publish mutation
  const publishMutation = useMutation({
    mutationFn: async () => {
      return catalogApi.publish(version, { notes: notes.trim() });
    },
    onSuccess: () => {
      // Invalidate queries
      queryClient.invalidateQueries({ queryKey: ['catalog', 'versions'] });
      queryClient.invalidateQueries({ queryKey: ['catalog', 'version', String(version)] });
      queryClient.invalidateQueries({ queryKey: ['catalog'] }); // Current catalog

      // Show success toast
      setShowSuccess(true);

      // Call onSuccess callback
      onSuccess?.();

      // Close dialog after a delay
      setTimeout(() => {
        setShowSuccess(false);
        handleClose();
      }, 2000);
    },
    onError: (err: any) => {
      setError(err.response?.data?.error || 'Failed to publish catalog version');
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    // Validate notes
    const trimmedNotes = notes.trim();
    if (!trimmedNotes) {
      setError('Publish notes are required. Please describe the changes in this version.');
      return;
    }

    if (trimmedNotes.length < 10) {
      setError('Please provide a more detailed description (at least 10 characters).');
      return;
    }

    publishMutation.mutate();
  };

  const handleClose = () => {
    if (publishMutation.isPending) return; // Prevent closing while publishing
    setError(null);
    setNotes('');
    setShowSuccess(false);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Dialog */}
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full">
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
            <div className="flex items-center space-x-2">
              <CheckCircle className="w-5 h-5 text-green-600" />
              <h2 className="text-lg font-semibold text-gray-900">
                Publish Catalog Version {version}
              </h2>
            </div>
            <button
              onClick={handleClose}
              disabled={publishMutation.isPending || showSuccess}
              className="p-1 rounded-md text-gray-400 hover:text-gray-500 hover:bg-gray-100 disabled:opacity-50"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="px-6 py-4 space-y-4">
            {/* Error message */}
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-start space-x-2">
                <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <p className="text-sm text-red-800 font-medium">Error</p>
                  <p className="text-sm text-red-700 mt-1">{error}</p>
                </div>
              </div>
            )}

            {/* Success message */}
            {showSuccess && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-start space-x-3">
                <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <p className="text-sm text-green-800 font-medium">
                    Successfully published!
                  </p>
                  <p className="text-sm text-green-700 mt-1">
                    Version {version} is now live. The previous published version has been
                    automatically unpublished.
                  </p>
                </div>
              </div>
            )}

            {!showSuccess && (
              <>
                {/* Warning box */}
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-start space-x-2">
                  <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <p className="text-sm text-amber-800 font-medium">
                      Important: Publishing this version will:
                    </p>
                    <ul className="text-sm text-amber-700 mt-2 space-y-1 list-disc list-inside">
                      <li>Make this version the active/current catalog</li>
                      <li>
                        Automatically unpublish any previously published version (it will
                        become a draft)
                      </li>
                      <li>Update all projects using the latest catalog version</li>
                    </ul>
                  </div>
                </div>

                {/* Publish notes field */}
                <div>
                  <label
                    htmlFor="publishNotes"
                    className="block text-sm font-medium text-gray-700 mb-2"
                  >
                    Publish Notes <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    id="publishNotes"
                    value={notes}
                    onChange={(e) => {
                      setNotes(e.target.value);
                      setError(null);
                    }}
                    rows={4}
                    className="input w-full resize-none"
                    placeholder="Describe the changes in this version...&#10;&#10;Examples:&#10;- Added new hero section variants&#10;- Updated color tokens for better accessibility&#10;- Fixed template slots configuration"
                    required
                    disabled={publishMutation.isPending}
                  />
                  <div className="flex items-center justify-between mt-1">
                    <p className="text-xs text-gray-500">
                      Minimum 10 characters. These notes will be saved with the version.
                    </p>
                    <p
                      className={`text-xs ${
                        notes.trim().length >= 10
                          ? 'text-green-600'
                          : 'text-gray-400'
                      }`}
                    >
                      {notes.trim().length} / 10
                    </p>
                  </div>
                </div>

                {/* Info box */}
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                  <p className="text-sm text-blue-800">
                    <strong>Note:</strong> Publish notes help your team track changes
                    across versions and understand what was modified. They will be visible
                    in the version history.
                  </p>
                </div>
              </>
            )}
          </form>

          {/* Footer */}
          {!showSuccess && (
            <div className="flex items-center justify-end space-x-3 px-6 py-4 border-t border-gray-200 bg-gray-50">
              <button
                type="button"
                onClick={handleClose}
                disabled={publishMutation.isPending}
                className="btn-secondary"
              >
                Cancel
              </button>
              <button
                type="submit"
                onClick={handleSubmit}
                disabled={publishMutation.isPending || !notes.trim()}
                className="btn-primary"
              >
                {publishMutation.isPending ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Publishing...
                  </>
                ) : (
                  <>
                    <CheckCircle className="w-4 h-4 mr-2" />
                    Publish Version
                  </>
                )}
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Success Toast (top-right corner) */}
      {showSuccess && (
        <div className="fixed top-4 right-4 z-[60] animate-fade-in">
          <div className="bg-green-600 text-white px-6 py-3 rounded-lg shadow-lg flex items-center space-x-3 min-w-[300px]">
            <CheckCircle className="w-5 h-5 flex-shrink-0" />
            <div className="flex-1">
              <p className="font-medium">Version {version} published!</p>
              <p className="text-sm text-green-100">
                Now live and ready to use
              </p>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
